import React, {  } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import CurrencyDropdown from './CurrencyDropdown';
import { Checkbox, InputValidation } from '../../../components/Forms';
import { colors, distances } from '../../../styles/constants';
import { FormValues } from './VirtualTerminal';
import { CurrencyCodeRecord } from 'currency-codes';
import { getPhonePrefixFromCurrency } from '../../../helpers/countryPrefix';
import { getProfilePaymentTypeLabel } from '../../../helpers/getProfilePaymentTypeLabel';
import { PaymentType } from '../../../types/checkout';
import { P } from '../../../components/Typography';

interface PaymentProvidersSelectProps {
    values: FormValues;
    currencies: CurrencyCodeRecord[];
    setFieldValue: (field: string, value: any) => void;
    paymentsMethods: {
        type: PaymentType;
        details?: {};
        currencies: string[];
    }[];
    validation?: InputValidation;
    touched: any;
}

const PaymentProvidersSelect = (
    {
        values,
        currencies,
        setFieldValue,
        paymentsMethods,
        validation,
        touched,
    }: PaymentProvidersSelectProps
) => {
    const { t } = useTranslation();

    const filteredPaymentsMethods = paymentsMethods
        .filter((payment) => payment.currencies.includes(values.currency.code))
        .sort((leftElement, rightElement) => leftElement.type.localeCompare(rightElement.type));

    return (
        <>
            {currencies.length > 1 && (
                <CurrencyDropdown
                    label={t('payments.virtual_terminal.fields.currency')}
                    name="currency"
                    value={values.currency.code}
                    options={currencies.map((c) => c.code)}
                    onChange={(value: string) => {
                        const currency = currencies.find((c) => c.code === value);
                        // Fix wrongly typed return value of setFieldValue function so we can chain the setFieldValue calls
                        // https://github.com/formium/formik/issues/2568
                        const setFieldValuePromise = setFieldValue as (
                            field: string,
                            value: any,
                            shouldValidate?: boolean | undefined
                        ) => Promise<undefined>;
                        setFieldValuePromise('currency', currency)
                            .then(() => setFieldValuePromise('countryPrefix', getPhonePrefixFromCurrency(currency)))
                            .then(() => setFieldValuePromise('payments', []));
                    }}
                    placeholder={t('payments.virtual_terminal.fields.currency')}
                />
            )}
            {filteredPaymentsMethods.map((payment, index) => {
                const isChecked = values.payments.includes(payment.type);
                return (
                    <ProviderOption
                        type="button"
                        key={index}
                        onClick={() => {
                            if (!isChecked) {
                                const payments = [...values.payments, payment.type];
                                const setFieldValuePromise = setFieldValue as (
                                    field: string,
                                    value: any,
                                    shouldValidate?: boolean | undefined
                                ) => Promise<undefined>;
                                setFieldValuePromise('payments', payments).then(() => {
                                    if (payments.find(p => p.startsWith('collector'))) {
                                        // Enable express if a collector payment was selected
                                        setFieldValue('express', true);
                                    }
                                });
                            } else {
                                setFieldValue(
                                    'payments',
                                    values.payments.filter((selectedPayment) => selectedPayment !== payment.type)
                                );
                            }
                        }}
                    >
                        <Checkbox
                            name={payment.type}
                            key={index}
                            label={getProfilePaymentTypeLabel(payment.type, t)}
                            checked={isChecked}
                            onChange={() => {
                                if (isChecked) {
                                    setFieldValue('payments', [...values.payments, payment.type]);
                                } else {
                                    setFieldValue(
                                        'payments',
                                        values.payments.filter((selectedPayment) => selectedPayment !== payment.type)
                                    );
                                }
                            }}
                        />
                    </ProviderOption>
                );
            })}
            {validation && validation.message && <Message className={validation.state}>{validation.message}</Message>}
        </>
    );
};

const ProviderOption = styled.button`
    display: flex;
    width: 100%;
    background: ${colors.backgroundAlt};
    border: 1px solid ${colors.backgroundAlt};
    outline: none;
    cursor: pointer;
    align-items: center;
    border-radius: 5px;
    justify-content: flex-start;
    padding: ${distances.small};

    &:hover {
        background: ${colors.backgroundInset};
        border: 1px solid ${colors.primary};
    }

    & + & {
        margin-top: ${distances.tiny};
    }
`;

const Message = styled.label`
    position: absolute;
    top: 68px;
    left: calc(68px + 24px);
    font-size: 11px;
    line-height: ${distances.small};

    &.invalid {
        color: ${colors.invalid};
    }

    &.valid {
        color: ${colors.valid};
    }
`;

export default PaymentProvidersSelect;
